import * as React from 'react';
import { Interpolate } from '@wg/wows-react-uikit';
import { withTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { ship, specialReward, currency } from '~/wowsEntities';
import CompensationEntity from '~/Components/Entities/CompensationEntity';
import store from '~/store';
import { useAppDispatch, useAppSelector } from '~/hooks';
import CountDown from '~/Components/CountDown/CountDown';
import { getStartBoosterProgressRestriction } from '~/utils/boosters';
import { openShipFeaturesDialog } from '~/store/dialogs';
import { getShipFromReward, getShipIdFromReward } from '~/utils/rewards';

const ConnectedCompensationEntity = () => (
    <Provider store={store}>
        <CompensationEntity />
    </Provider>
);

export interface TextProps {
    str: string,
    clickableVehicles?: boolean,
}

function Text(props: TextProps) {
    const { str, clickableVehicles = false } = props;
    const dispatch = useAppDispatch();
    const appState = useAppSelector((state) => state.app);
    const {
        specialRewards,
        mainRewardShipId,
        mainRewardShipName,
        shipLevels,
        progressFinishTime,
        closeTime,
        freeLevels,
        starterPacksMaxLevel,
        boosters,
        shipPreviewBackground,
        secondaryShipPreviewBackground,
        levelsCompletedBySpendingDoubloons,
    } = appState;

    const lastLevel = shipLevels.length - 1;

    const showShipFeaturesDialog = React.useCallback((shipId: number | string) => {
        dispatch(openShipFeaturesDialog(shipId));
    }, [dispatch]);

    const showMainShipFeaturesDialog = React.useCallback(() => {
        dispatch(openShipFeaturesDialog(mainRewardShipId));
    }, [dispatch, mainRewardShipId]);

    const getSpecialRewardByIndex = React.useCallback((rewardIndex: number) => {
        const rewardShipId = specialRewards?.[rewardIndex] && getShipIdFromReward(specialRewards[rewardIndex]);
        if (rewardShipId) {
            return ship(
                rewardShipId,
                true,
                (secondaryShipPreviewBackground && clickableVehicles)
                    ? () => showShipFeaturesDialog(Number(rewardShipId))
                    : undefined
            );
        }

        return specialReward(specialRewards?.[rewardIndex]);
    }, [specialRewards, showShipFeaturesDialog, secondaryShipPreviewBackground, clickableVehicles]);

    const getSpecialRewardShipCompensationByIndex = React.useCallback((rewardIndex: number) => {
        const rewardShip = specialRewards?.[rewardIndex] && getShipFromReward(specialRewards[rewardIndex]);
        if (rewardShip?.compensation) {
            return Object.entries(rewardShip.compensation).map((entry) => {
                return currency(entry[0], Number(entry[1]));
            });
        }
        return null;
    }, [specialRewards]);

    return (
        <Interpolate
            str={str}
            ship={ship(
                mainRewardShipId,
                true,
                (shipPreviewBackground && clickableVehicles)
                    ? showMainShipFeaturesDialog
                    : undefined
            )}
            shipWithIcons={ship(
                mainRewardShipId,
                false,
                (shipPreviewBackground && clickableVehicles)
                    ? showMainShipFeaturesDialog
                    : undefined
            )}
            totalCount={lastLevel}
            canAchieveCount={freeLevels}
            compensation={<ConnectedCompensationEntity />}
            levelCount={starterPacksMaxLevel}
            firstSpecialReward={getSpecialRewardByIndex(0)}
            secondSpecialReward={getSpecialRewardByIndex(1)}
            secondSpecialRewardCompensation={getSpecialRewardShipCompensationByIndex(1)}
            firstSpecialRewardStage={specialRewards?.[0]?.shipLevelNumber}
            secondSpecialRewardStage={specialRewards?.[1]?.shipLevelNumber}
            progressFinishTime={<CountDown date={progressFinishTime} disableClock disabledTooltip />}
            closeTime={<CountDown date={closeTime} disableClock disabledTooltip />}
            name={mainRewardShipName}
            progressRestriction={getStartBoosterProgressRestriction(boosters)}
            premiumLevelsCount={levelsCompletedBySpendingDoubloons}
        />
    );
}

export default withTranslation()(Text);
